// Helpers para reproducir el video grabado del relay (HLS histórico) a partir
// de un log de la biblioteca. El Worker arma el playlist on-the-fly desde el
// wall-clock del log (`/api/play?...&at=<ISO UTC>&to=<ISO UTC>`). Ver la guía
// de integración del relay para el modelo temporal y el mapa de canales.

import config from "../../../config";

// Ventana del scrubber y del playlist HLS alrededor del timecode: 30 min antes
// + 30 min después. El Worker genera el manifiesto desde índices (no descarga
// los medios), por lo que un manifiesto de una hora sigue siendo pequeño. Usar el
// mismo rango evita sustituir el MediaSource cada pocos segundos durante play.
export const CLIP_BEFORE_SEC = 30 * 60;
export const CLIP_AFTER_SEC = 30 * 60;
export const RELAY_PLAYLIST_BEFORE_SEC = CLIP_BEFORE_SEC;
export const RELAY_PLAYLIST_AFTER_SEC = CLIP_AFTER_SEC;

// Margen del borde live (ms). El archivo grabado sólo llega hasta
// ~ahora − (latencia SRT + cierre de segmento + intervalo de scan del catálogo
// del relay). Si el objetivo `at` apunta a "ahora" (p.ej. un log recién creado),
// cae en un instante que aún no existe en el archivo y el player esperaría ~esa
// latencia a que se grabe. Al apuntar cerca de "ahora" restamos este margen para
// pedir un instante que YA existe → carga inmediata. Un `at` claramente pasado no
// se ve afectado. Ver el clamp en RelayClipPlayer.
export const LIVE_EDGE_MARGIN_MS = 10_000;

const RELAY_RECORDING_CACHE_MAX = 250;
const RELAY_RECORDING_RANGE_CACHE_MAX = 20;
const relayRecordingCache = new Map<string, string>();
const relayChannelRecordingCache = new Map<
  string,
  { fromMs: number; toMs: number; recording: string }[]
>();

export interface RelayProgramFragment {
  start?: number;
  duration?: number;
  programDateTime?: number | string | Date | null;
}

function relayWindow(at: Date, beforeSec: number, afterSec: number) {
  return {
    from: new Date(at.getTime() - beforeSec * 1000),
    to: new Date(at.getTime() + afterSec * 1000),
  };
}

function relayRecordingCacheKey(
  project: string,
  channel: string,
  at: string,
  from: string,
  to: string,
) {
  return `${project}|${channel}|${at}|${from}|${to}`;
}

function relayChannelRecordingCacheKey(project: string, channel: string) {
  return `${project}|${channel}`;
}

function rememberRelayRecording(
  project: string,
  channel: string,
  at: string,
  from: string,
  to: string,
  recording: string,
) {
  const key = relayRecordingCacheKey(project, channel, at, from, to);
  if (!relayRecordingCache.has(key) && relayRecordingCache.size >= RELAY_RECORDING_CACHE_MAX) {
    const oldestKey = relayRecordingCache.keys().next().value;
    if (oldestKey) relayRecordingCache.delete(oldestKey);
  }
  relayRecordingCache.set(key, recording);

  const fromMs = Date.parse(from);
  const toMs = Date.parse(to);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return;
  const start = Math.min(fromMs, toMs);
  const end = Math.max(fromMs, toMs);
  const channelKey = relayChannelRecordingCacheKey(project, channel);
  const ranges = relayChannelRecordingCache.get(channelKey) || [];
  const existing = ranges.find(
    (range) => range.recording === recording && start <= range.toMs && end >= range.fromMs,
  );
  if (existing) {
    existing.fromMs = Math.min(existing.fromMs, start);
    existing.toMs = Math.max(existing.toMs, end);
  } else {
    ranges.push({ fromMs: start, toMs: end, recording });
    while (ranges.length > RELAY_RECORDING_RANGE_CACHE_MAX) ranges.shift();
  }
  relayChannelRecordingCache.set(channelKey, ranges);
}

function cachedRelayRecording(
  at: Date,
  channel: string,
  beforeSec: number,
  afterSec: number,
): string | null {
  const { from, to } = relayWindow(at, beforeSec, afterSec);
  const exact = relayRecordingCache.get(
    relayRecordingCacheKey(
      config.RELAY_PROJECT_ID,
      channel,
      at.toISOString(),
      from.toISOString(),
      to.toISOString(),
    ),
  );
  if (exact) return exact;

  const atMs = at.getTime();
  return (
    [
      ...(relayChannelRecordingCache.get(
        relayChannelRecordingCacheKey(config.RELAY_PROJECT_ID, channel),
      ) || []),
    ]
      .reverse()
      .find((range) => atMs >= range.fromMs && atMs <= range.toMs)?.recording || null
  );
}

function isRecordingId(value: string | null | undefined): value is string {
  return (
    !!value &&
    (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) ||
      /^[0-9A-HJKMNP-TV-Z]{26}$/i.test(value))
  );
}

function extractRecordingFromPlaylist(
  playlistText: string | null | undefined,
  project: string,
  channel: string,
): string | null {
  if (!playlistText) return null;

  const recordingParam = playlistText.match(/[?&]recording=([^"&\s]+)/)?.[1];
  if (recordingParam) {
    try {
      const decoded = decodeURIComponent(recordingParam);
      return isRecordingId(decoded) ? decoded : null;
    } catch {
      return isRecordingId(recordingParam) ? recordingParam : null;
    }
  }

  for (const match of playlistText.matchAll(/https?:\/\/[^\s"']+/g)) {
    let parsed: URL;
    try {
      parsed = new URL(match[0]);
    } catch {
      continue;
    }

    const segments = parsed.pathname
      .split("/")
      .filter(Boolean)
      .map((segment) => {
        try {
          return decodeURIComponent(segment);
        } catch {
          return segment;
        }
      });
    for (let index = 0; index < segments.length; index += 1) {
      if (segments[index] !== channel) continue;
      const candidate = index > 0 ? segments[index - 1] : null;
      if (isRecordingId(candidate) && candidate !== project) return candidate;
    }

    const candidate = segments.find((segment) => isRecordingId(segment) && segment !== project);
    if (candidate) return candidate;
  }

  return null;
}

export function rememberRelayRecordingFromPlaylist(url: string, playlistText?: string | null) {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.pathname !== "/api/play") return null;

  const project = parsed.searchParams.get("project") || config.RELAY_PROJECT_ID;
  const channel = parsed.searchParams.get("channel");
  const at = parsed.searchParams.get("at");
  const from = parsed.searchParams.get("from");
  const to = parsed.searchParams.get("to");
  if (!channel || !at || !from || !to) return null;

  const requestedRecording = parsed.searchParams.get("recording");
  const recording =
    (isRecordingId(requestedRecording) ? requestedRecording : null) ||
    extractRecordingFromPlaylist(playlistText, project, channel) ||
    null;
  if (!recording) return null;

  rememberRelayRecording(project, channel, at, from, to, recording);
  return recording;
}

function toProgramDateTimeMs(value: RelayProgramFragment["programDateTime"]): number | null {
  if (value == null) return null;
  const ms =
    value instanceof Date ? value.getTime() : typeof value === "number" ? value : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function clampSeconds(value: number, min: number, max?: number): number {
  const lower = Math.max(min, value);
  return Number.isFinite(max) ? Math.min(lower, Math.max(min, max as number)) : lower;
}

export function resolveRelayProgramOffset(
  fragments: readonly RelayProgramFragment[] | undefined,
  atMs: number,
  totalDurationSec?: number,
  fallbackSec = CLIP_BEFORE_SEC,
): number {
  const total = Number.isFinite(totalDurationSec)
    ? Math.max(0, totalDurationSec as number)
    : undefined;
  const fallback = clampSeconds(fallbackSec, 0, total);
  if (!fragments?.length || !Number.isFinite(atMs)) return fallback;

  let previous: {
    pdtMs: number;
    start: number;
    duration: number;
  } | null = null;
  let next: {
    pdtMs: number;
    start: number;
    duration: number;
  } | null = null;

  for (const fragment of fragments) {
    const pdtMs = toProgramDateTimeMs(fragment.programDateTime);
    if (pdtMs == null) continue;

    const start = Number.isFinite(fragment.start) ? Math.max(0, fragment.start as number) : 0;
    const duration = Number.isFinite(fragment.duration)
      ? Math.max(0, fragment.duration as number)
      : 0;
    const endPdtMs = pdtMs + duration * 1000;
    const end = duration > 0 ? start + duration : total;

    if (pdtMs <= atMs && (duration <= 0 || atMs <= endPdtMs)) {
      const offset = start + (atMs - pdtMs) / 1000;
      return clampSeconds(offset, start, end);
    }

    if (pdtMs <= atMs) {
      if (!previous || pdtMs > previous.pdtMs) previous = { pdtMs, start, duration };
    } else if (!next || pdtMs < next.pdtMs) {
      next = { pdtMs, start, duration };
    }
  }

  if (previous) {
    const previousEnd = previous.start + previous.duration;
    const previousWallEndMs = previous.pdtMs + previous.duration * 1000;
    const distanceToPrevious = Math.max(0, atMs - previousWallEndMs);
    const distanceToNext = next ? Math.max(0, next.pdtMs - atMs) : Number.POSITIVE_INFINITY;
    if (!next || distanceToPrevious <= distanceToNext) {
      return clampSeconds(previousEnd, 0, total);
    }
  }

  if (next) return clampSeconds(next.start, 0, total);
  return fallback;
}

// Deriva el wall-clock (UTC) del log. Replica la lógica de LogCard#headerDate:
// los timestamps llegan de la DB sin zona pero representan UTC, así que les
// forzamos `Z` antes de parsear para no correr el día en browsers al oeste de UTC.
export function getLogWallClock(log: any): Date | null {
  let raw = log?.start_at ?? log?.startAt ?? log?.created_at ?? log?.createdAt;
  if (raw == null || raw === "") {
    const candidate = log?.tcIn;
    if (candidate != null && candidate !== "") {
      if (typeof candidate === "string") {
        if (candidate.includes("-") || candidate.includes("T")) {
          raw = candidate;
        }
      } else {
        raw = candidate;
      }
    }
  }
  if (raw == null || raw === "") return null;
  let input: string | number | Date = raw;
  if (typeof raw === "string") {
    const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(raw);
    const isIsoDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw);
    if (isIsoDateTime && !hasTz) input = `${raw}Z`;
  }
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Mapea los tags de stream/cámara del log al canal de video del relay.
// 1) RELAY_CHANNEL_MAP (env): substring-de-tag → channel (gana el primer match).
// 2) Heurística: un tag que mencione "2"/"secundario"/"second" → video-2.
// 3) Fallback: RELAY_DEFAULT_CHANNEL (video-1).
export function resolveRelayChannel(streamTagLabels: string[]): string {
  const labels = streamTagLabels.map((l) => (l || "").toLowerCase()).filter(Boolean);

  for (const [needle, channel] of Object.entries(config.RELAY_CHANNEL_MAP)) {
    const n = needle.toLowerCase();
    if (n && labels.some((l) => l.includes(n))) return channel;
  }

  if (labels.some((l) => /(?:^|[^0-9])2(?:[^0-9]|$)|secundari|second/.test(l))) {
    return "video-2";
  }

  return config.RELAY_DEFAULT_CHANNEL;
}

// Arma la URL del único media playlist HLS (video H.264 + audio AAC embebido)
// para un instante dado.
export function buildRelayClipUrl(
  at: Date,
  channel: string,
  {
    beforeSec = CLIP_BEFORE_SEC,
    afterSec = CLIP_AFTER_SEC,
    recording = null,
  }: {
    beforeSec?: number;
    afterSec?: number;
    recording?: string | null;
  } = {},
): string {
  // `at` = el momento del log (el worker resuelve la grabación por acá, siempre
  // tiene segmento → no 404). `from`/`to` definen la ventana ±30 min; el worker
  // la recorta a lo grabado si no hay tanto historial antes.
  const { from, to } = relayWindow(at, beforeSec, afterSec);
  const params = new URLSearchParams({
    project: config.RELAY_PROJECT_ID,
    channel,
    at: at.toISOString(),
    from: from.toISOString(),
    to: to.toISOString(),
  });
  const recordingUid = recording || cachedRelayRecording(at, channel, beforeSec, afterSec);
  if (recordingUid) params.set("recording", recordingUid);
  return `${config.RELAY_WORKER_BASE}/api/play?${params.toString()}`;
}
